import React, { useEffect, useRef } from 'react';

import { formatLinkBlockToUrl } from '$utilities/format';
import { SimpleImage } from '$partials/media';
import ModuleContainer from '$partials/modules/container.jsx';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/partials/modules.d.ts').TeamMemberWithMedia}
 */
const TeamMemberWithMedia = ({
    link,
    teamMember,
}) => {
    /** @type {React.RefObject<HTMLLIElement>} */
    const containerRef = useRef(null);
    /** @type {React.RefObject<HTMLVideoElement>} */
    const videoRef = useRef(null);

    useEffect(() => {
        if (containerRef.current && videoRef.current) {
            const enterListener = containerRef.current.addEventListener('mouseenter', () => {
                videoRef.current.play();
            });

            const leaveListener = containerRef.current.addEventListener('mouseleave', () => {
                videoRef.current.pause();
                videoRef.current.currentTime = 0;
            });

            return () => {
                window.removeEventListener('mouseenter', enterListener);
                window.removeEventListener('mouseleave', leaveListener);
            };
        }
    }, [containerRef, videoRef]);

    return (
        <li
            className='group/team relative flex w-full bg-stone rounded mb-8 expand-interaction'
            ref={containerRef}>
            {link &&
                <div
                    aria-hidden={true}
                    className='absolute left-0 translate-x-[-44px] px-15 sm:px-20'>
                    <div className='pt-8 group-hover/team:translate-x-[4px] transition-transform'>
                        →
                    </div>
                </div>
            }
            <div className='w-2/8 p-6'>
                <div className='relative aspect-[4/5] rounded overflow-hidden'>
                    {teamMember.profileImage?.image &&
                        <SimpleImage
                            {...teamMember.profileImage?.image}
                            className='w-full h-full object-cover' />
                    }
                    {teamMember.profileImage?.video &&
                        <video
                            className='video-play-on-hover w-full h-full object-cover'
                            loop={true}
                            muted={true}
                            playsInline={true}
                            poster={teamMember.profileImage.video.poster}
                            preload='auto'
                            ref={videoRef}
                            src={teamMember.profileImage.video.src} />
                    }
                </div>
            </div>

            <div className='w-4/8 py-6 pl-12 pr-6'>
                <h4 className='mb-12'>
                    {link &&
                        <a
                            className='expand-interaction__action'
                            href={link}
                            rel={teamMember.link?.linkTo === 'custom_url' ? 'noreferrer' : undefined}
                            target={teamMember.link?.linkTo === 'custom_url' ? '_blank' : undefined}>
                            {teamMember.name}
                        </a>
                    }
                    {!link &&
                        <>
                            {teamMember.name}
                        </>
                    }
                </h4>

                {teamMember.role &&
                    <div>
                        {teamMember.role}
                    </div>
                }
                {teamMember.companyName &&
                    <div>
                        {teamMember.companyName}
                    </div>
                }
            </div>

            <div className='flex items-start justify-end w-2/8 p-6'>
                <div className='relative aspect-[1/1] w-40 sm:w-[50px] rounded overflow-hidden'>
                    {teamMember.companyLogo?.image &&
                        <SimpleImage
                            {...teamMember.companyLogo?.image}
                            className='w-full h-full object-contain object-right-top' />
                    }
                </div>
            </div>
        </li>
    );
};

/**
 * @type {import('$types/components/partials/modules.d.ts').TeamBlock}
 */
const TeamBlock = ({
    ctaLabel,
    ctaLink,
    heading,
    media,
    teams,
    text,
}) => {
    return (
        <ModuleContainer
            ctaLabel={ctaLabel}
            ctaLink={ctaLink}
            heading={heading}
            text={text}>
            {media &&
                <div className='pb-30'>
                    <div className='overflow-hidden rounded'>
                        {media?.image &&
                            <SimpleImage
                                {...media.image}
                                className='w-full'
                                style={{ aspectRatio: `${media.image.width}/${media.image.height}` }} />
                        }
                        {media?.video &&
                            <video
                                autoPlay={true}
                                className='w-full pointer-events-none'
                                loop={true}
                                muted={true}
                                playsInline={true}
                                poster={media.video.poster}
                                src={media.video.src}
                                style={{ aspectRatio: `${media.video.width}/${media.video.height}` }} />
                        }
                    </div>
                </div>
            }

            <ul className='pb-30'>
                {teams?.map((team, i) => {
                    return (
                        <li
                            className='border-t border-black py-8 mb-30'
                            key={i}>
                            {team.text &&
                                <h3 className='pt-button-baseline'>
                                    {team.heading}
                                </h3>
                            }

                            <div className='grid grid-cols-1 sm:grid-cols-2 gap-8 pt-button-baseline'>
                                <div>
                                    {team.text &&
                                        <div
                                            className='rich-text max-w-text-length mb-button-baseline sm:mb-0 sm:mr-30'
                                            dangerouslySetInnerHTML={{ __html: team.text || '' }} />
                                    }
                                    {!team.text &&
                                        <h3 className='mb-button-baseline sm:mb-0 sm:mr-30'>
                                            {team.heading}
                                        </h3>
                                    }
                                </div>

                                <ul>
                                    {team.teamMembers?.map((teamMember, i) => {
                                        const imageLayout = Boolean(teamMember.profileImage || teamMember.companyLogo);
                                        const link = formatLinkBlockToUrl(teamMember.link);

                                        if (imageLayout) {
                                            return (
                                                <TeamMemberWithMedia
                                                    key={i}
                                                    link={link}
                                                    teamMember={teamMember} />
                                            );
                                        } else {
                                            return (
                                                <li key={i}>
                                                    <h4 className='inline'>
                                                        {link &&
                                                            <a
                                                                className='underline'
                                                                href={link}
                                                                rel='noreferrer'
                                                                target='_blank'>
                                                                {teamMember.name}
                                                            </a>
                                                        }
                                                        {!link &&
                                                            <>
                                                                {teamMember.name}
                                                            </>
                                                        }
                                                    </h4>
                                                    {teamMember.role &&
                                                        <span>
                                                            , {teamMember.role}
                                                        </span>
                                                    }
                                                    {teamMember.companyName &&
                                                        <span>
                                                            , {teamMember.companyName}
                                                        </span>
                                                    }
                                                </li>
                                            );
                                        }
                                    })}
                                </ul>
                            </div>
                        </li>
                    );
                })}
            </ul>
        </ModuleContainer>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

export default TeamBlock;
